import React, { useContext, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { useTheme } from 'styled-components';
import {
  ADD_LOGIN_USER,
  ADD_NOTIFICATIONS,
  ADD_TOKEN,
  RootState
} from './store';
import RoutesParams from './routes';
import {} from './components/index';
import { getCookie, getNotifications } from './services';
import { AppContext } from './shared/providers/AppProvider';
import { OccurrenceProvider } from './pages/RefactorOccurrences/providers/OccurrenceProvider';
import { DashboardProvider } from './pages/Dashboard/providers/DashboardProvider';
import { BlogProvider } from './pages/Blog/providers/BlogProvider';
import { logoDark } from './assets';
import { LoadingImageStyled, LoadingImageWrapper } from './index.styled';
import SocketContext from './context/socket';

const NOTIFICATION_SOUND_URL =
  'https://s3.us-east-2.amazonaws.com/br.com.fogocruzado/notification/notification.wav';
const NOTIFICATION_SOUND_VOLUME = 0.15;

const App: React.FC = ({}) => {
  const { appUsecase, apiRepository } = useContext(AppContext);
  const { listOfOccurrences } = useContext(SocketContext);
  const dispatch = useDispatch();
  const theme = useTheme();
  const { token } = useSelector((state: RootState) => state.clickState);

  const [loading, setLoading] = React.useState(true);
  const previousNotificationKeysRef = useRef<string[]>([]);
  const hasInitializedNotificationsRef = useRef(false);
  const previousSocketEventCounterRef = useRef<number | null>(null);
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);

  function getNotificationKey(notification: any, index: number) {
    const id = notification?.id;

    if (id !== undefined && id !== null && `${id}`.length > 0) {
      return `id:${id}`;
    }

    return `fallback:${notification?.createdAt || ''}:${
      notification?.title || ''
    }:${notification?.message || ''}:${index}`;
  }

  async function unlockAudio() {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (!notificationAudioRef.current) {
        notificationAudioRef.current = new Audio(NOTIFICATION_SOUND_URL);
        notificationAudioRef.current.preload = 'auto';
        notificationAudioRef.current.volume = NOTIFICATION_SOUND_VOLUME;
      }

      const audio = notificationAudioRef.current;

      audio.muted = true;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;
    } catch (_) {
      try {
        notificationAudioRef.current?.load();
      } catch (error) {
        console.warn('Unable to preload notification sound:', error);
      }
    }
  }

  async function playNotificationSound() {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (!notificationAudioRef.current) {
        notificationAudioRef.current = new Audio(NOTIFICATION_SOUND_URL);
        notificationAudioRef.current.preload = 'auto';
        notificationAudioRef.current.volume = NOTIFICATION_SOUND_VOLUME;
      }

      const audio = notificationAudioRef.current;
      audio.currentTime = 0;
      await audio.play();
    } catch (error) {
      console.warn('Unable to play notification sound:', error);
    }
  }

  function isNotificationSoundEnabled() {
    if (typeof window === 'undefined') {
      return true;
    }

    return localStorage.getItem('notificationSoundEnabled') !== 'false';
  }

  async function refreshToken() {
    let token = getCookie();

    if (!!token) {
      let resp = await apiRepository.refreshToken(token);
      resp.handle({
        onSuccess(data) {
          appUsecase.setToken(data?.token);
          dispatch({ type: ADD_LOGIN_USER, user: data.user });
          dispatch({ type: ADD_TOKEN, token: data.token });
        },
        onFailure(_) {
          appUsecase.setToken('');
          dispatch({ type: ADD_LOGIN_USER, user: {} });
          dispatch({ type: ADD_TOKEN, token: '' });
        }
      });
    }

    setLoading(false);
  }

  useEffect(() => {
    refreshToken();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleUserInteraction = () => {
      unlockAudio();
    };

    window.addEventListener('pointerdown', handleUserInteraction, {
      passive: true
    });
    window.addEventListener('keydown', handleUserInteraction, {
      passive: true
    });

    return () => {
      window.removeEventListener('pointerdown', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      notificationAudioRef.current?.pause();
      notificationAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      previousNotificationKeysRef.current = [];
      hasInitializedNotificationsRef.current = false;
      previousSocketEventCounterRef.current = null;
      return;
    }

    const isFirstSocketCounterRead =
      previousSocketEventCounterRef.current === null;
    const previousSocketCounter = previousSocketEventCounterRef.current ?? 0;
    const hasSocketUpdate = listOfOccurrences > previousSocketCounter;

    previousSocketEventCounterRef.current = listOfOccurrences;

    if (!isFirstSocketCounterRead && !hasSocketUpdate) {
      return;
    }

    const refreshNotifications = async () => {
      try {
        const response = await getNotifications(token);
        if (!Array.isArray(response)) {
          return;
        }

        dispatch({ type: ADD_NOTIFICATIONS, notifications: response });

        const currentNotificationKeys = response.map((notification, index) =>
          getNotificationKey(notification, index)
        );

        if (hasInitializedNotificationsRef.current) {
          if (hasSocketUpdate && isNotificationSoundEnabled()) {
            playNotificationSound();
          }
        }

        previousNotificationKeysRef.current = currentNotificationKeys;
        hasInitializedNotificationsRef.current = true;
      } catch (error) {
        console.warn('Unable to refresh notifications:', error);
      }
    };

    refreshNotifications();
  }, [dispatch, listOfOccurrences, token]);

  return (
    <>
      {loading ? (
        <LoadingImageWrapper>
          <LoadingImageStyled src={logoDark} />
        </LoadingImageWrapper>
      ) : (
        <OccurrenceProvider>
          <DashboardProvider>
            <BlogProvider>
              <BrowserRouter>
                <RoutesParams />
              </BrowserRouter>
            </BlogProvider>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 5000,
                style: {
                  background: theme.colors.surface,
                  color: theme.colors.textPrimary,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.radii.md,
                  boxShadow: theme.shadows.sm,
                  padding: theme.spacing.md,
                  fontFamily: theme.typography.fontFamily
                },
                success: {
                  iconTheme: {
                    primary: theme.colors.success,
                    secondary: theme.colors.white
                  },
                  style: {
                    border: `1px solid ${theme.colors.success}`
                  }
                },
                error: {
                  iconTheme: {
                    primary: theme.colors.danger,
                    secondary: theme.colors.white
                  },
                  style: {
                    border: `1px solid ${theme.colors.danger}`
                  }
                }
              }}
            />
          </DashboardProvider>
        </OccurrenceProvider>
      )}
    </>
  );
};

export default App;
